import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, MessageCircle, Phone, Youtube } from 'lucide-react';
import { LANGS, useLang } from '../i18n';
import { waConfigured, waLink } from '../config/whatsapp';
import { useSiteContent } from '../utils/siteContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const { t, lang, setLang } = useLang();
  const cms = useSiteContent('footer');
  const [version, setVersion] = useState(null);

  useEffect(() => {
    fetch(`${API}/system/version`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setVersion(d))
      .catch(() => setVersion(null));
  }, []);

  const COLS = [
    {
      title: t('foot.col.destinations'),
      links: [
        { label: t('foot.dest.st'), to: '/destinations/sao-tome-principe/sao-tome', testid: 'footer-dest-sao-tome' },
        { label: t('foot.dest.pr'), to: '/destinations/sao-tome-principe/principe', testid: 'footer-dest-principe' },
        { label: t('foot.dest.cv'), to: '/destinations/cabo-verde', testid: 'footer-dest-cabo-verde' },
      ],
    },
    {
      title: t('foot.col.explore'),
      links: [
        { label: t('nav.experiences'), to: '/experiences', testid: 'footer-link-experiences' },
        { label: t('foot.explore.packages'), to: '/full-packages', testid: 'footer-link-full-packages' },
        { label: t('foot.explore.cruise'), to: '/cruise', testid: 'footer-link-cruise' },
        { label: t('nav.stays'), to: '/accommodations', testid: 'footer-link-stays' },
        { label: t('nav.transfers'), to: '/transfers', testid: 'footer-link-transfers' },
        { label: t('nav.flights'), to: '/flights', testid: 'footer-link-flights' },
        { label: t('rent.title'), to: '/rent-a-car', testid: 'footer-link-rentacar' },
        { label: t('foot.explore.plan'), to: '/build-your-journey', testid: 'footer-link-plan-trip' },
      ],
    },
    {
      title: t('foot.col.trade'),
      links: [
        { label: t('foot.trade.agencies'), to: '/travel-trade', testid: 'footer-link-travel-trade' },
        { label: t('foot.trade.portfolio'), to: '/travel-trade', testid: 'footer-link-product-portfolio' },
        { label: t('foot.trade.sheet'), to: '/travel-trade#trade-request', testid: 'footer-link-product-sheet' },
        { label: t('foot.trade.net'), to: '/travel-trade#trade-request', testid: 'footer-link-net-rates' },
        { label: t('foot.trade.tailor'), to: '/travel-trade#trade-request', testid: 'footer-link-tailor-made' },
        { label: t('foot.trade.join'), to: '/join', testid: 'footer-link-become-partner' },
        { label: t('foot.trade.login'), to: '/login?role=partner', testid: 'footer-link-partner-login' },
      ],
    },
    {
      title: t('foot.col.network'),
      links: [
        { label: t('foot.network.partners'), to: '/partners', testid: 'footer-link-network' },
        { label: t('foot.network.operators'), to: '/partners?type=operator', testid: 'footer-link-operators' },
        { label: t('foot.network.stays'), to: '/partners?type=accommodation', testid: 'footer-link-network-stays' },
        { label: t('foot.network.guides'), to: '/partners?type=guide', testid: 'footer-link-network-guides' },
      ],
    },
    {
      title: t('foot.col.eco'),
      links: [
        { label: t('foot.co.about'), to: '/about', testid: 'footer-link-about' },
        { label: t('foot.co.how'), to: '/#how-it-works', testid: 'footer-link-how-it-works' },
        { label: t('foot.co.info'), to: '/company-information', testid: 'footer-link-company-info' },
        { label: t('foot.co.contact'), to: '/contact', testid: 'footer-link-company-contact' },
      ],
    },
    {
      title: t('foot.col.support'),
      links: [
        { label: t('foot.sup.help'), to: '/contact', testid: 'footer-link-help' },
        { label: t('foot.sup.contact'), to: '/contact', testid: 'footer-link-contact' },
        { label: t('foot.sup.booking'), to: '/contact', testid: 'footer-link-booking-support' },
        { label: t('foot.sup.manage'), to: '/my-trip', testid: 'footer-link-manage-request' },
        { label: t('foot.sup.emergency'), to: '/contact', testid: 'footer-link-emergency' },
      ],
    },
    {
      title: t('foot.col.legal'),
      links: [
        { label: t('foot.leg.terms'), to: '/terms', testid: 'footer-link-terms' },
        { label: t('foot.leg.privacy'), to: '/privacy', testid: 'footer-link-privacy' },
        { label: t('foot.leg.cancellation'), to: '/cancellation-policy', testid: 'footer-link-cancellation' },
        { label: t('foot.leg.cookies'), to: '/cookies', testid: 'footer-link-cookies' },
        { label: t('foot.leg.affiliate'), to: '/affiliate-disclosure', testid: 'footer-link-affiliate' },
        { label: t('foot.leg.partner'), to: '/partner-terms', testid: 'footer-link-partner-terms' },
      ],
    },
  ];

  const SOCIALS = [
    { icon: Instagram, label: 'Instagram' },
    { icon: Facebook, label: 'Facebook' },
    { icon: Youtube, label: 'YouTube' },
  ];

  return (
    <footer id="contactos" data-testid="main-footer" className="relative overflow-hidden bg-eco-deep text-white/70">
      <div className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 rounded-full bg-eco-green/25 blur-[110px]" />
      <div className="eco-container relative pt-14 pb-8">
        <div className="grid gap-10 lg:grid-cols-[1.3fr_2.6fr]">
          <div>
            <Link to="/" className="inline-flex text-white" data-testid="footer-logo">
              <span className="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-3 shadow-lg ring-1 ring-white/10">
                <img src="/logo-eco-full.png" alt="Eco Collection" className="h-28 w-auto object-contain" />
              </span>
            </Link>
            <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.22em] text-eco-gold" data-testid="footer-tagline">{cms.tagline || 'Travel · Explore · Experience'}</p>
            <p className="mt-1.5 text-[11px] font-medium tracking-[0.06em] text-white/55" data-testid="footer-network-line">Eco Collection Travel Network — A BSGOTECH Initiative</p>
            <p className="mt-2 max-w-xs text-xs leading-relaxed text-white/70">
              {cms.description || t('footer.desc')}
            </p>
            <div className="mt-5 space-y-2">
              <a href={`tel:${(cms.phone || '+4917684450554').replace(/\s/g, '')}`} data-testid="footer-contact-phone" className="flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-eco-gold">
                <Phone size={14} /> {cms.phone || '+49 176 844 50554'}
              </a>
              <a href={`mailto:${cms.email || 'Admin@bsgotech.com'}`} data-testid="footer-contact-email" className="flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-eco-gold">
                <Mail size={14} /> {cms.email || 'Admin@bsgotech.com'}
              </a>
            </div>
            <div className="mt-5 flex gap-2.5" data-testid="footer-social">
              {SOCIALS.map(({ icon: Icon, label }) => (
                <span key={label} data-testid={`footer-social-${label.toLowerCase()}`} title={t('footer.soon')} aria-label={`${label} — ${t('footer.soon')}`} className="grid h-10 w-10 cursor-default place-items-center rounded-full border border-white/15 text-white/45">
                  <Icon size={16} />
                </span>
              ))}
              {waConfigured && (
                <a href={waLink(t('wa.general'))} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" aria-label="WhatsApp" className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 transition-colors duration-300 hover:border-eco-gold hover:text-eco-gold">
                  <MessageCircle size={16} />
                </a>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-7 gap-y-9 min-[420px]:grid-cols-2 sm:grid-cols-3 xl:grid-cols-4">
            {COLS.map((col) => (
              <div key={col.title} className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/85">{col.title}</p>
                <ul className="mt-3.5 space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link.testid}>
                      <Link to={link.to} data-testid={link.testid} className="block break-words text-sm text-white/60 transition-colors hover:text-eco-gold">{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-12 border-t border-white/10 pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <p className="text-xs text-white/45" data-testid="footer-copyright">© {new Date().getFullYear()} Eco Collection. {t('foot.rights')}</p>
            <nav className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs" data-testid="footer-bottom-legal">
              <Link to="/privacy" className="text-white/55 transition-colors hover:text-eco-gold">{t('foot.leg.privacy')}</Link>
              <Link to="/terms" className="text-white/55 transition-colors before:mr-4 before:text-white/20 before:content-['·'] hover:text-eco-gold">{t('foot.leg.terms')}</Link>
              <Link to="/company-information" className="text-white/55 transition-colors before:mr-4 before:text-white/20 before:content-['·'] hover:text-eco-gold">{t('foot.co.info')}</Link>
              <Link to="/cookies" className="text-white/55 transition-colors before:mr-4 before:text-white/20 before:content-['·'] hover:text-eco-gold">{t('foot.leg.cookies')}</Link>
              <button type="button" data-testid="footer-cookie-preferences" onClick={() => window.dispatchEvent(new CustomEvent('eco:cookie-preferences'))} className="text-white/55 transition-colors before:mr-4 before:text-white/20 before:content-['·'] hover:text-eco-gold">
                {t('foot.cookies.btn')}
              </button>
            </nav>
            <div className="flex items-center gap-1.5" data-testid="footer-languages">
              {LANGS.map((l) => (
                <button key={l.id} type="button" onClick={() => setLang(l.id)} data-testid={`footer-lang-${l.id}`} className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase transition-colors ${lang === l.id ? 'bg-eco-gold text-eco-deep' : 'text-white/50 hover:text-eco-gold'}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] text-white/35">
            <span data-testid="footer-powered">Powered by BSGOTECH</span>
            <span data-testid="footer-currencies">{t('foot.currencies')}</span>
            <Link to="/login?role=admin" data-testid="footer-admin-link" className="transition-colors before:mr-3 before:text-white/15 before:content-['·'] hover:text-eco-gold">Admin</Link>
            {version && (
              <span data-testid="footer-system-version" className="inline-flex items-center gap-1.5">
                <span className="text-white/15">·</span>
                <span className={`h-1.5 w-1.5 rounded-full ${version.db === 'connected' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                Eco Collection {version.version} · API {version.api} · {version.db === 'connected' ? t('footer.online') : t('footer.offline')}
              </span>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};
